import { useEffect, useState } from "react";
import axios from "axios";
import { ZoomIn } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL;

export const MESH_INFO = {
  petscreen: { title: "PetScreen mesh", caption: "Heavy-duty, tear-resistant screen mesh. This is mesh only, not a pet door or metal pet guard." },
  solar: { title: "Solar Screen mesh", caption: "Darker mesh that blocks up to 80% of sun heat and glare." },
};

let photosPromise = null;
const loadPhotos = () => {
  if (!photosPromise) photosPromise = axios.get(`${BACKEND_URL}/api/public/mesh-photos`).then((r) => r.data).catch(() => null);
  return photosPromise;
};
const fallback = { petscreen: { thumb_url: "/mesh/pet-thumb.webp", full_url: "/mesh/pet-full.webp" },
  solar: { thumb_url: "/mesh/solar-thumb.webp", full_url: "/mesh/solar-full.webp" } };
const abs = (u) => (u?.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

export const meshKindFromName = (name = "") => {
  const n = name.toLowerCase();
  if (n.includes("window")) return null;
  if (n.includes("petscreen") || n.includes("pet screen")) return "petscreen";
  if (n.includes("solar")) return "solar";
  return null;
};

// Tap-to-zoom thumbnail + one-line caption for a door mesh upgrade.
export function MeshPhoto({ kind, compact = false }) {
  const [photos, setPhotos] = useState(fallback);
  const [open, setOpen] = useState(false);
  useEffect(() => { loadPhotos().then((d) => d && setPhotos(d)); }, []);
  const p = photos[kind];
  const info = MESH_INFO[kind];
  if (!p || !info) return null;
  return (
    <div className={`flex items-center gap-3 ${compact ? "mt-1" : "my-3"}`} data-testid={`mesh-photo-${kind}`}>
      <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="relative shrink-0 rounded-full overflow-hidden border border-zinc-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#207024]"
        style={{ width: compact ? 40 : 56, height: compact ? 40 : 56 }}
        aria-label={`See a close-up of ${info.title}`} data-testid={`mesh-thumb-${kind}`}>
        <img src={abs(p.thumb_url)} alt={info.title} width={compact ? 40 : 56} height={compact ? 40 : 56}
          loading="lazy" decoding="async" className="w-full h-full object-cover" />
        <span className="absolute bottom-0.5 right-0.5 bg-black/60 rounded-full p-0.5"><ZoomIn className="w-3 h-3 text-white" /></span>
      </button>
      <p className={`${compact ? "text-[11px]" : "text-xs"} text-zinc-600 leading-snug`} data-testid={`mesh-caption-${kind}`}>{info.caption}</p>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md p-3" data-testid={`mesh-dialog-${kind}`}>
          <DialogTitle className="text-base">{info.title} close-up</DialogTitle>
          {open && <img src={abs(p.full_url)} alt={`${info.title} close-up`} className="w-full max-w-sm mx-auto rounded-full aspect-square object-cover bg-white" />}
          <p className="text-sm text-zinc-600">{info.caption}</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
